import { useMemo, useState } from "react";
import { useData } from "../context/DataContext";
import {
  months,
  projectMonthlyRaw,
  projectRawTotal,
  capacityMatrix,
  firmMonthly,
  firmGrandTotal,
} from "../lib/calc";
import { fmtMoney, fmtPct, fmtMonthShort, contractLabel } from "../lib/format";
import { firstName } from "../lib/names";
import { downloadCsv } from "../lib/csv";
import InfoTip from "../components/InfoTip";
import { Download, ChevronDown, ChevronRight, ChevronUp } from "lucide-react";
import DragScrollContainer from "../components/DragScrollContainer";
import { usePrefs, applyOrder, moveInList } from "../lib/prefs";

// Monk Mackenzie "worksheet" — a single master table that mirrors the source
// spreadsheet: per project, a header row, scope $ rows, a subtotal, then
// people % rows. Months across the top. Grand totals at the bottom.

const CHIP = {
  Yes: "chip-yes",
  Tentative: "chip-tentative",
  No: "chip-no",
};

export default function Sheet() {
  const { data } = useData();
  const m = months(data);
  // Projects open COLLAPSED — 28 projects × scopes × people is an unusable
  // page otherwise. Open blocks are remembered for the browser session only,
  // so the sheet always opens tidy.
  const [expanded, setExpanded] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("sheetExpanded") || "{}"); } catch { return {}; }
  });
  const [showPeople, setShowPeople] = useState(true);
  const { prefs, savePref } = usePrefs();

  const peopleMap = useMemo(
    () => Object.fromEntries(data.people.map((p) => [p.id, p.name])),
    [data.people]
  );
  const principalMap = useMemo(
    () => Object.fromEntries(data.principals.map((p) => [p.id, p.name])),
    [data.principals]
  );

  const monthlyTotals = useMemo(() => firmMonthly(data, false), [data]);
  const grandTotal = useMemo(() => firmGrandTotal(data, false), [data]);
  const fteByMonth = useMemo(() => {
    // total firm FTE deployed per month
    const matrix = capacityMatrix(data);
    return Object.fromEntries(
      m.map((mo) => [
        mo,
        Object.values(matrix).reduce((s, row) => s + (row[mo] || 0), 0),
      ])
    );
  }, [data, m]);

  // Per-user sheet order, saved server-side via /api/prefs. Deliberately its
  // OWN key: My Projects orders one principal's subset, and ranking the whole
  // firm by a subset list would drag those projects to the top of the sheet.
  const projects = useMemo(
    () => applyOrder(data.projects, prefs?.sheetProjectOrder),
    [data.projects, prefs]
  );
  const moveProject = (index, delta) => {
    const next = moveInList(projects.map((p) => p.id), index, delta);
    if (next) savePref("sheetProjectOrder", next);
  };

  const persistExpanded = (next) => {
    setExpanded(next);
    try { sessionStorage.setItem("sheetExpanded", JSON.stringify(next)); } catch { /* session only */ }
  };
  const toggle = (id) => persistExpanded({ ...expanded, [id]: !expanded[id] });
  const expandAll = () => persistExpanded(Object.fromEntries(projects.map((p) => [p.id, true])));
  const collapseAll = () => persistExpanded({});
  const openCount = projects.filter((p) => expanded[p.id]).length;

  const exportCsv = () => {
    const header = ["Number", "Project", "Principal", "Scope", "Contract", "Prob.", "Notes", ...m, "Total"];
    const rows = [header];
    // Always every row, whatever is collapsed on screen.
    for (const p of projects) {
      const principalName = principalMap[p.principalId] || "";
      const subtotalByMonth = projectMonthlyRaw(p, m);
      // scope rows
      for (const s of p.scopes) {
        const total = Object.values(s.revenue || {}).reduce((a, b) => a + (b || 0), 0);
        rows.push([
          p.id, p.name, principalName, s.stage, p.contract,
          fmtPct(s._originalProbability ?? s.probability ?? 1, 0),
          "",
          ...m.map((mo) => s.revenue?.[mo] ?? 0),
          total,
        ]);
      }
      rows.push([
        p.id, p.name, principalName, "SubTotal", p.contract, "", p.note || "",
        ...m.map((mo) => subtotalByMonth[mo] ?? 0),
        projectRawTotal(p),
      ]);
      // people rows
      for (const a of p.allocations || []) {
        rows.push([
          p.id, p.name, principalName, peopleMap[a.personId] || a.personId, "", "", "",
          ...m.map((mo) => (a.load?.[mo] ? `${Math.round(a.load[mo]*100)}%` : "")),
          "",
        ]);
      }
    }
    rows.push(["", "", "", "Monthly total", "", "", "", ...m.map((mo) => monthlyTotals[mo] || 0), grandTotal]);
    downloadCsv("worksheet.csv", rows);
  };

  // shared column widths
  const colHead = "px-3 py-2.5 text-[11px] font-display tracked-tight uppercase text-[var(--soft-fg)] border-b border-[var(--rule-strong)] bg-white";
  const cellNum = "px-3 py-1.5 text-right font-mono-num text-[12px] text-[var(--ink)] whitespace-nowrap";

  return (
    <div className="space-y-8" data-testid="sheet-view">
      <div className="flex justify-between items-end flex-wrap gap-4">
        <div>
          <div className="eyebrow">Worksheet</div>
          <h2 className="section-h mt-1">Master forecasting sheet</h2>
          <div className="text-xs font-mono-num text-[var(--soft-fg)] mt-1">
            One source of truth — projects × scope × months · with people allocations underneath.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center border border-[var(--rule-strong)]">
            <button
              onClick={expandAll}
              className="px-3 py-1.5 text-xs font-display tracked-tight uppercase hover:bg-[var(--paper-soft)]"
              data-testid="sheet-expand-all"
            >
              Expand all
            </button>
            <button
              onClick={collapseAll}
              className="px-3 py-1.5 text-xs font-display tracked-tight uppercase border-l border-[var(--rule-strong)] hover:bg-[var(--paper-soft)]"
              data-testid="sheet-collapse-all"
            >
              Collapse all
            </button>
          </div>
          <span className="text-[11px] font-mono-num text-[var(--soft-fg)]" data-testid="sheet-open-count">
            {openCount} of {projects.length} open
          </span>
          <label className="inline-flex items-center gap-2 text-xs eyebrow">
            <input
              type="checkbox"
              checked={showPeople}
              onChange={(e) => setShowPeople(e.target.checked)}
              data-testid="sheet-toggle-people"
              className="accent-[var(--ink)]"
            />
            Show people rows
          </label>
          <button
            onClick={exportCsv}
            className="inline-flex items-center gap-2 px-3 py-1.5 border border-[var(--rule-strong)] text-xs font-display tracked-tight uppercase hover:bg-[var(--paper-soft)]"
            data-testid="sheet-export-csv"
          >
            <Download size={12} /> CSV
          </button>
        </div>
      </div>

      <DragScrollContainer
        className="border border-[var(--rule)] overflow-auto thin-scroll bg-white max-h-[calc(100vh-190px)]"
        data-testid="sheet-table-wrap"
      >
        <table className="w-full text-sm border-collapse" data-testid="sheet-table">
          <thead className="sticky top-0 z-10">
            <tr>
              <th className={`${colHead} text-left sticky left-0 z-20 bg-white min-w-[150px]`}>Number</th>
              <th className={`${colHead} text-left sticky left-[150px] z-20 bg-white min-w-[200px]`}>Project / Scope</th>
              <th className={`${colHead} text-left min-w-[140px]`}>Principal</th>
              <th className={`${colHead} text-left min-w-[90px]`}>
                <span className="inline-flex items-center">
                  Contract
                  <InfoTip side="bottom" label="Contract status">
                    <strong>Yes</strong> — signed contract.<br/>
                    <strong>Tentative</strong> — likely but not yet signed.<br/>
                    <strong>No</strong> — speculative / paused.
                  </InfoTip>
                </span>
              </th>
              <th className={`${colHead} text-right min-w-[70px]`}>
                <span className="inline-flex items-center">
                  Prob.
                  <InfoTip side="bottom" label="Probability">
                    Likelihood (0–100%) that this scope's fee will actually be invoiced. Used by the <em>Probability-weighted</em> scenario.
                  </InfoTip>
                </span>
              </th>
              <th className={`${colHead} text-left min-w-[170px]`}>Notes</th>
              {m.map((mo) => (
                <th key={mo} className={`${colHead} text-right min-w-[88px]`}>{fmtMonthShort(mo)}</th>
              ))}
              <th className={`${colHead} text-right min-w-[100px] border-l border-[var(--rule-strong)]`}>Total</th>
            </tr>
          </thead>
          <tbody>
            {projects.map((p, idx) => {
              const isCollapsed = !expanded[p.id];
              const subtotal = projectMonthlyRaw(p, m);
              const projTotal = projectRawTotal(p);
              const principalName = principalMap[p.principalId] || "—";
              return (
                <ProjectBlock
                  key={p.id}
                  project={p}
                  months={m}
                  isCollapsed={isCollapsed}
                  onToggle={() => toggle(p.id)}
                  onMove={(delta) => moveProject(idx, delta)}
                  canMoveUp={idx > 0}
                  canMoveDown={idx < projects.length - 1}
                  subtotal={subtotal}
                  projTotal={projTotal}
                  principalName={principalName}
                  peopleMap={peopleMap}
                  showPeople={showPeople}
                  cellNum={cellNum}
                />
              );
            })}

            {/* Grand totals */}
            <tr className="bg-[var(--paper-soft)] border-t-2 border-[var(--ink)]">
              <td className="px-3 py-3 sticky left-0 bg-[var(--paper-soft)]" />
              <td className="px-3 py-3 sticky left-[150px] bg-[var(--paper-soft)]">
                <span className="font-display tracked-tight uppercase text-xs inline-flex items-center">
                  Monthly revenue
                  <InfoTip side="top" label="Monthly revenue">
                    Sum of every project's $ for that month, under the active Scenario.
                  </InfoTip>
                </span>
              </td>
              <td colSpan={4} />
              {m.map((mo) => (
                <td key={mo} className={`${cellNum} font-medium`} data-testid={`sheet-total-${mo}`}>
                  {monthlyTotals[mo] ? fmtMoney(monthlyTotals[mo]) : "—"}
                </td>
              ))}
              <td className={`${cellNum} font-medium border-l border-[var(--rule-strong)]`} data-testid="sheet-grand-total">
                {fmtMoney(grandTotal)}
              </td>
            </tr>
            {showPeople && (
              <tr className="border-t border-[var(--rule)]">
                <td className="px-3 py-2 sticky left-0 bg-white" />
                <td className="px-3 py-2 sticky left-[150px] bg-white">
                  <span className="eyebrow inline-flex items-center">
                    Team FTE deployed
                    <InfoTip side="top" label="Team FTE deployed">
                      Sum of every person's load that month, in full-time-equivalent units. 5.00 = the equivalent of 5 full people working that month.
                    </InfoTip>
                  </span>
                </td>
                <td colSpan={4} />
                {m.map((mo) => (
                  <td key={mo} className={`${cellNum} text-[var(--soft-fg)]`} data-testid={`sheet-fte-${mo}`}>
                    {fteByMonth[mo] ? fteByMonth[mo].toFixed(2) : "—"}
                  </td>
                ))}
                <td className="border-l border-[var(--rule-strong)]" />
              </tr>
            )}
          </tbody>
        </table>
      </DragScrollContainer>

      <div className="text-xs font-mono-num text-[var(--soft-fg)]">
        Scope rows show <span className="text-[var(--ink)]">$ per month</span> (with probability) ·
        SubTotal sums the project's scopes · People rows show <span className="text-[var(--ink)]">% of full-time</span> per month.
        Scenario &amp; period selectors apply.
      </div>
    </div>
  );
}

function ProjectBlock({
  project, months, isCollapsed, onToggle, onMove, canMoveUp, canMoveDown,
  subtotal, projTotal, principalName, peopleMap, showPeople, cellNum,
}) {
  const chip = CHIP[project.contract] || "";

  return (
    <>
      {/* Project header */}
      <tr className="bg-[#F7F7F5] border-t-2 border-[var(--rule-strong)]" data-testid={`sheet-project-${project.id}`}>
        <td className="px-3 py-2.5 sticky left-0 bg-[#F7F7F5] font-mono-num text-[11px] text-[var(--muted-fg)] whitespace-nowrap">
          <span className="inline-flex items-center gap-1">
            {/* Sheet order — per-user, saved server-side */}
            <span className="flex flex-col">
              <button onClick={() => onMove(-1)} disabled={!canMoveUp}
                className="text-[var(--soft-fg)] hover:text-[var(--ink)] disabled:opacity-25"
                title="Move up" aria-label={`Move ${project.name} up`} data-testid={`sheet-move-up-${project.id}`}>
                <ChevronUp size={12} />
              </button>
              <button onClick={() => onMove(1)} disabled={!canMoveDown}
                className="text-[var(--soft-fg)] hover:text-[var(--ink)] disabled:opacity-25"
                title="Move down" aria-label={`Move ${project.name} down`} data-testid={`sheet-move-down-${project.id}`}>
                <ChevronDown size={12} />
              </button>
            </span>
            <button
              onClick={onToggle}
              className="inline-flex items-center gap-1 hover:text-[var(--ink)]"
              data-testid={`sheet-toggle-${project.id}`}
              aria-label={`${isCollapsed ? "Expand" : "Collapse"} ${project.name}`}
            >
              {isCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
              {project.id}
            </button>
          </span>
        </td>
        <td className="px-3 py-2.5 sticky left-[150px] bg-[#F7F7F5] font-display text-sm text-[var(--ink)]">
          {project.name}
        </td>
        <td className="px-3 py-2.5 text-[12px] text-[var(--ink)]">{principalName}</td>
        <td className="px-3 py-2.5">
          {chip ? <span className={`chip ${chip}`}>{contractLabel(project.contract)}</span> : null}
        </td>
        <td className="px-3 py-2.5" />
        <td className="px-3 py-2.5 text-[11px] font-mono-num text-[var(--soft-fg)] truncate max-w-[200px]" title={project.note}>
          {project.note || ""}
        </td>
        {/* Collapsed blocks carry the project's monthly subtotals on the header
            row, so every figure still reads without opening anything. */}
        {months.map((mo) => (
          <td
            key={mo}
            className={`${cellNum} ${isCollapsed ? "font-medium" : "text-[var(--soft-fg)]"}`}
            data-testid={isCollapsed ? `sheet-header-sub-${project.id}-${mo}` : undefined}
          >
            {isCollapsed
              ? (subtotal[mo] ? fmtMoney(subtotal[mo]) : <span className="text-[var(--soft-fg)]">—</span>)
              : null}
          </td>
        ))}
        <td className={`${cellNum} font-medium text-[var(--ink)] border-l border-[var(--rule-strong)]`}>
          {fmtMoney(projTotal)}
        </td>
      </tr>

      {!isCollapsed && (
        <>
          {/* Scope rows */}
          {project.scopes.map((s) => {
            const total = months.reduce((a, mo) => a + (s.revenue?.[mo] ?? 0), 0);
            const prob = s._originalProbability ?? s.probability ?? 1;
            return (
              <tr key={s.id} className="border-t border-[var(--rule)] hover:bg-[var(--paper-soft)]"
                  data-testid={`sheet-scope-${project.id}-${s.id}`}>
                <td className="px-3 py-1.5 sticky left-0 bg-white" />
                <td className="px-3 py-1.5 sticky left-[150px] bg-white text-[12px] text-[var(--ink)] pl-6">
                  {s.stage}
                </td>
                <td className="px-3 py-1.5" />
                <td className="px-3 py-1.5" />
                <td className="px-3 py-1.5 text-right font-mono-num text-[11px] text-[var(--muted-fg)]">
                  {fmtPct(prob, 0)}
                </td>
                <td className="px-3 py-1.5" />
                {months.map((mo) => (
                  <td key={mo} className={cellNum}>
                    {s.revenue?.[mo] ? fmtMoney(s.revenue[mo]) : <span className="text-[var(--soft-fg)]">—</span>}
                  </td>
                ))}
                <td className={`${cellNum} border-l border-[var(--rule-strong)]`}>
                  {fmtMoney(total)}
                </td>
              </tr>
            );
          })}

          {/* SubTotal row */}
          <tr className="border-t border-[var(--rule)] bg-[#EFEFEC]" data-testid={`sheet-subtotal-${project.id}`}>
            <td className="px-3 py-2 sticky left-0 bg-[#EFEFEC]" />
            <td className="px-3 py-2 sticky left-[150px] bg-[#EFEFEC] text-[12px] font-display tracked-tight uppercase pl-6">
              <span className="inline-flex items-center">
                SubTotal
                <InfoTip side="top" label="SubTotal">
                  Sum of every scope's $ for this project, month by month. The total at the right is the project's full forecast value.
                </InfoTip>
              </span>
            </td>
            <td colSpan={4} className="bg-[#EFEFEC]" />
            {months.map((mo) => (
              <td key={mo} className={`${cellNum} font-medium`}>
                {subtotal[mo] ? fmtMoney(subtotal[mo]) : <span className="text-[var(--soft-fg)]">—</span>}
              </td>
            ))}
            <td className={`${cellNum} font-medium border-l border-[var(--rule-strong)]`}>
              {fmtMoney(projTotal)}
            </td>
          </tr>

          {/* People rows */}
          {showPeople && project.allocations && project.allocations.length > 0 && (
            <>
              <tr className="border-t border-[var(--rule)]">
                <td className="px-3 py-1.5 sticky left-0 bg-white" />
                <td className="px-3 py-1.5 sticky left-[150px] bg-white text-[10px] eyebrow text-[var(--soft-fg)] pl-6">People · % per month</td>
                <td colSpan={4} />
                {months.map((mo) => <td key={mo} />)}
                <td className="border-l border-[var(--rule-strong)]" />
              </tr>
              {project.allocations.map((a, i) => (
                <tr key={`${project.id}-${a.personId}-${i}`} className="border-t border-[var(--rule)] hover:bg-[var(--paper-soft)]"
                    data-testid={`sheet-person-${project.id}-${a.personId}`}>
                  <td className="px-3 py-1.5 sticky left-0 bg-white" />
                  <td className="px-3 py-1.5 sticky left-[150px] bg-white text-[12px] text-[var(--muted-fg)] pl-6"
                      title={peopleMap[a.personId] || a.personId}>
                    {firstName(peopleMap[a.personId] || a.personId)}
                  </td>
                  <td colSpan={4} />
                  {months.map((mo) => {
                    const load = a.load?.[mo] || 0;
                    const isOver = load > 1.0001;
                    return (
                      <td key={mo} className={`${cellNum} ${isOver ? "text-[#6B2F2A] font-medium" : "text-[var(--muted-fg)]"}`}>
                        {load ? `${Math.round(load*100)}%` : <span className="text-[var(--soft-fg)]">—</span>}
                      </td>
                    );
                  })}
                  <td className="border-l border-[var(--rule-strong)]" />
                </tr>
              ))}
            </>
          )}
        </>
      )}
    </>
  );
}
